import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, ClockIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { DateRange } from 'react-day-picker';
import { useGT } from 'gt-react';
import { cn } from '@/lib/utils';

export interface DateTimeRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface TimeValue {
  hours: number;
  minutes: number;
  seconds: number;
}

interface DateTimeRangePickerProps {
  value?: DateTimeRange;
  onChange: (dateTimeRange: DateTimeRange | undefined) => void;
  className?: string;
  placeholder?: string;
  /** Whether to show the time picker toggle. Defaults to true. */
  showTimeToggle?: boolean;
  /** Default state of the time picker toggle. Defaults to false (date-only mode). */
  defaultIncludeTime?: boolean;
}

function parseTimeString(timeStr: string): TimeValue {
  const parts = timeStr.split(':');
  return {
    hours: parseInt(parts[0] || '0', 10) || 0,
    minutes: parseInt(parts[1] || '0', 10) || 0,
    seconds: parseInt(parts[2] || '0', 10) || 0,
  };
}

function formatTimeValue(time: TimeValue): string {
  return `${String(time.hours).padStart(2, '0')}:${String(time.minutes).padStart(2, '0')}:${String(time.seconds).padStart(2, '0')}`;
}

function getTimeFromDate(date: Date | undefined): TimeValue {
  if (!date) {
    return { hours: 0, minutes: 0, seconds: 0 };
  }
  return {
    hours: date.getHours(),
    minutes: date.getMinutes(),
    seconds: date.getSeconds(),
  };
}

function applyTimeToDate(date: Date, time: TimeValue): Date {
  const result = new Date(date);
  result.setHours(time.hours, time.minutes, time.seconds, 0);
  return result;
}

export function DateTimeRangePicker({
  value,
  onChange,
  className,
  placeholder,
  showTimeToggle = true,
  defaultIncludeTime = false,
}: DateTimeRangePickerProps) {
  const gt = useGT();
  const resolvedPlaceholder =
    placeholder ??
    gt('Pick a date range', {
      context: 'date range picker placeholder when no range selected',
    });
  const [includeTime, setIncludeTime] = useState(defaultIncludeTime);
  const [nextSelection, setNextSelection] = useState<'start' | 'end'>(() =>
    value?.from && !value?.to ? 'end' : 'start',
  );
  const [startTime, setStartTime] = useState<TimeValue>(() =>
    getTimeFromDate(value?.from),
  );
  const [endTime, setEndTime] = useState<TimeValue>(() =>
    value?.to
      ? getTimeFromDate(value.to)
      : { hours: 23, minutes: 59, seconds: 59 },
  );

  useEffect(() => {
    if (!value?.from && !value?.to) {
      setNextSelection('start');
    } else if (value?.from && !value?.to) {
      setNextSelection('end');
    }
  }, [value?.from, value?.to]);

  // Sync internal time state when external value changes
  useEffect(() => {
    if (value?.from) {
      setStartTime(getTimeFromDate(value.from));
    }
    if (value?.to) {
      setEndTime(getTimeFromDate(value.to));
    }
  }, [value?.from, value?.to]);

  const handleDateRangeChange = (
    _dateRange: DateRange | undefined,
    selectedDay?: Date,
  ) => {
    if (!selectedDay) {
      onChange(undefined);
      setNextSelection('start');
      return;
    }

    const startOfDay: TimeValue = { hours: 0, minutes: 0, seconds: 0 };
    const endOfDay: TimeValue = { hours: 23, minutes: 59, seconds: 59 };
    const buildStartDate = (day: Date) =>
      applyTimeToDate(day, includeTime ? startTime : startOfDay);
    const buildEndDate = (day: Date) =>
      applyTimeToDate(day, includeTime ? endTime : endOfDay);

    if (nextSelection === 'start') {
      const newFrom = buildStartDate(selectedDay);
      const currentTo = value?.to;
      const newTo = currentTo && currentTo >= newFrom ? currentTo : undefined;
      onChange({ from: newFrom, to: newTo });
      setNextSelection('end');
      return;
    }

    if (!value?.from) {
      const newFrom = buildStartDate(selectedDay);
      onChange({ from: newFrom, to: undefined });
      setNextSelection('end');
      return;
    }

    const newTo = buildEndDate(selectedDay);
    if (newTo < value.from) {
      const newFrom = buildStartDate(selectedDay);
      const swappedTo = buildEndDate(value.from);
      onChange({ from: newFrom, to: swappedTo });
    } else {
      onChange({ from: value.from, to: newTo });
    }
    setNextSelection('start');
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = parseTimeString(e.target.value);
    setStartTime(timeValue);

    if (value?.from) {
      const newFrom = applyTimeToDate(value.from, timeValue);
      onChange({ ...value, from: newFrom });
    }
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = parseTimeString(e.target.value);
    setEndTime(timeValue);

    if (value?.to) {
      const newTo = applyTimeToDate(value.to, timeValue);
      onChange({ ...value, to: newTo });
    }
  };

  const handleTimeToggle = (checked: boolean) => {
    setIncludeTime(checked);

    if (!value?.from && !value?.to) {
      return;
    }

    if (checked) {
      // Switching to time mode - apply current time values
      const newFrom = value.from
        ? applyTimeToDate(value.from, startTime)
        : undefined;
      const newTo = value.to ? applyTimeToDate(value.to, endTime) : undefined;
      onChange({ from: newFrom, to: newTo });
    } else {
      // Switching to date-only mode - reset to full day
      const newFrom = value.from
        ? applyTimeToDate(value.from, { hours: 0, minutes: 0, seconds: 0 })
        : undefined;
      const newTo = value.to
        ? applyTimeToDate(value.to, { hours: 23, minutes: 59, seconds: 59 })
        : undefined;
      onChange({ from: newFrom, to: newTo });
    }
  };

  function formatDateTime(date: Date) {
    if (includeTime) {
      return format(date, 'MMM d yyyy, HH:mm:ss');
    }
    return format(date, 'MMM d, yyyy');
  }

  function getRangeLabel() {
    if (!value?.from && !value?.to) {
      return resolvedPlaceholder;
    }

    if (value?.from && !value?.to) {
      return formatDateTime(value.from);
    }

    if (!value?.from && value?.to) {
      return formatDateTime(value.to);
    }

    if (value?.from && value?.to) {
      const fromDate = value.from;
      const toDate = value.to;

      if (includeTime) {
        // With time, always show full format
        return `${formatDateTime(fromDate)} - ${formatDateTime(toDate)}`;
      }

      // Date-only mode - use compact formatting
      const sameYear = fromDate.getFullYear() === toDate.getFullYear();
      const sameMonth = sameYear && fromDate.getMonth() === toDate.getMonth();

      if (sameMonth) {
        return `${format(fromDate, 'MMM d')} - ${format(toDate, 'd, yyyy')}`;
      }

      if (sameYear) {
        return `${format(fromDate, 'MMM d')} - ${format(toDate, 'MMM d, yyyy')}`;
      }

      return `${format(fromDate, 'MMM d, yyyy')} - ${format(toDate, 'MMM d, yyyy')}`;
    }

    return resolvedPlaceholder;
  }

  const rangeLabel = getRangeLabel();

  // Convert DateTimeRange to react-day-picker DateRange (date only)
  const calendarValue: DateRange | undefined = value
    ? { from: value.from, to: value.to }
    : undefined;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-empty={!value}
          className={cn(
            'data-[empty=true]:text-muted-foreground w-[280px] justify-start text-left font-normal h-8',
            includeTime && value?.from && value?.to && 'w-auto min-w-[400px]',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          <span className="truncate">{rangeLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3">
          <Calendar
            mode="range"
            selected={calendarValue}
            defaultMonth={value?.from || new Date()}
            onSelect={handleDateRangeChange}
            required
          />

          {showTimeToggle && (
            <div className="border-t pt-3 mt-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="include-time"
                  className="text-sm font-medium flex items-center gap-2"
                >
                  <ClockIcon className="h-4 w-4" />
                  {gt('Include specific times', {
                    context: 'date range picker, toggle time of day',
                  })}
                </Label>
                <Switch
                  id="include-time"
                  checked={includeTime}
                  onCheckedChange={handleTimeToggle}
                />
              </div>

              {includeTime && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="start-time" className="text-xs">
                      {gt('Start Time', {
                        context: 'date range picker start time field',
                      })}
                    </Label>
                    <Input
                      id="start-time"
                      type="time"
                      step="1"
                      value={formatTimeValue(startTime)}
                      onChange={handleStartTimeChange}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="end-time" className="text-xs">
                      {gt('End Time', {
                        context: 'date range picker end time field',
                      })}
                    </Label>
                    <Input
                      id="end-time"
                      type="time"
                      step="1"
                      value={formatTimeValue(endTime)}
                      onChange={handleEndTimeChange}
                      className="h-8"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
